import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Org } from '../../../enum';
import { APIStatus } from '../../../enum';
import { getCSV, getTableFilters, notifyMissingFields, removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

export class UnlockCodeDetails {
  total_count: number = 0;
  id: string | undefined = undefined;
  unlock_code: string | undefined = undefined;
  org_code: Org | null = null;
  org_name: string | undefined = undefined;
  org_timezone_name: string | null = null;
  active: boolean | undefined = undefined;
  chat: boolean | undefined = undefined;
  expiration_date: string | undefined | null = undefined;
  unique_use_count: number = 0;
  max_use_count: number | null = null;
  cohort_count: number | null = null;
  created: string | undefined = undefined;
  cohort_ids: string[] = [];
  description: string = '';
  use_in_dashboard_filter: boolean = true;
}

export const UNLOCK_CODE_MODAL = {
  IDLE: 'idle',
  EDIT_UNLOCK_CODE: 'edit_unlock_code',
} as const;

export type UNLOCK_CODE_MODAL = (typeof UNLOCK_CODE_MODAL)[keyof typeof UNLOCK_CODE_MODAL];

type UnlockCodeSliceType = {
  unlock_codes: UnlockCodeDetails[];
  csv: string;
  tableFiltersInfo: string;
  requestUnlockCodeParam: RequestParams<UnlockCodeDetails>;
  unlockCodeApiStatus: APIStatus;
  unlockCodeDetailsModal: UNLOCK_CODE_MODAL;
  unlockCodeDetails: UnlockCodeDetails;
};

const initialState: UnlockCodeSliceType = {
  unlock_codes: [],
  csv: '',
  tableFiltersInfo: '',
  requestUnlockCodeParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<UnlockCodeDetails>('unlock_code', 'ascend'),
    filteredInfo: {},
  },
  unlockCodeApiStatus: APIStatus.IDLE,
  unlockCodeDetailsModal: UNLOCK_CODE_MODAL.IDLE,
  unlockCodeDetails: new UnlockCodeDetails(),
};

export const fetchUnlockCodes = createAsyncThunk<UnlockCodeDetails[], undefined, AsyncThunkConfig>(
  'adminUnlockCodes/fetchUnlockCodes',
  async (_, thunkAPI) => {
    try {
      const requestUnlockCodeParam = thunkAPI.getState().adminUnlockCodesSlice.requestUnlockCodeParam;
      const response = (await axios.post('v0_unlock_codes', requestUnlockCodeParam)) as UnlockCodeDetails[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminUnlockCodesSlice', 'fetchUnlockCodes']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const exportUnlockCodes = createAsyncThunk<UnlockCodeDetails[], undefined, AsyncThunkConfig>(
  'adminUnlockCodes/exportUnlockCodes',
  async (_, thunkAPI) => {
    try {
      const tableParams = thunkAPI.getState().adminUnlockCodesSlice.requestUnlockCodeParam;
      const requestUnlockCodeParam = {
        sortedInfo: tableParams.sortedInfo,
        filteredInfo: tableParams.filteredInfo,
      };

      const filtersInfo = getTableFilters(requestUnlockCodeParam.filteredInfo);
      thunkAPI.dispatch(setTableFiltersInfo(filtersInfo));

      const response = (await axios.post('v0_unlock_codes', requestUnlockCodeParam)) as UnlockCodeDetails[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminUnlockCodesSlice', 'exportUnlockCodes']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

class UnlockCodeUpsertResponse {
  unlock_code_id: string = '';
  is_update: boolean = false;
}

const validateUnlockCode = (unlockCodeDetails: UnlockCodeDetails) => {
  const missingRequiredFields = [];
  if (!unlockCodeDetails.unlock_code) missingRequiredFields.push('unlock code');
  if (!unlockCodeDetails.org_code) missingRequiredFields.push('organization');
  if (!unlockCodeDetails.description) missingRequiredFields.push('description');
  if (unlockCodeDetails.active !== false && unlockCodeDetails.active !== true) missingRequiredFields.push('active');

  if (missingRequiredFields.length > 0) {
    return notifyMissingFields(missingRequiredFields);
  } else {
    return true;
  }
};

export const upsertUnlockCode = createAsyncThunk<UnlockCodeUpsertResponse, UnlockCodeDetails, AsyncThunkConfig>(
  'adminUnlockCodes/upsertUnlockCode',
  async (unlockCode, thunkAPI) => {
    try {
      if (validateUnlockCode(unlockCode)) {
        const response = (await axios.post('v0_unlock_code_upsert', unlockCode)) as UnlockCodeUpsertResponse;
        if (response) {
          thunkAPI.dispatch(fetchUnlockCodes());
          thunkAPI.dispatch(closeUnlockCodeDetailsModal());
          notification.success({
            title: `Unlock code ${response.is_update ? 'updated' : 'added'} successfully`,
            description: 'Success',
          });
          return response;
        }
      }
      return new UnlockCodeUpsertResponse();
    } catch (e) {
      logError(e, ['adminUnlockCodesSlice', 'upsertUnlockCode']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminUnlockCodesSlice = createSlice({
  name: 'adminUnlockCodes',
  initialState,
  reducers: {
    handleManagerTableChange: (state, action: PayloadAction<TableChange<UnlockCodeDetails>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<UnlockCodeDetails>;
      state.requestUnlockCodeParam.pagination = action.payload.pagination;
      state.requestUnlockCodeParam.sortedInfo = sorter;
      state.requestUnlockCodeParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestUnlockCodeParam.pagination = initialState.requestUnlockCodeParam.pagination;
      state.requestUnlockCodeParam.sortedInfo = initialState.requestUnlockCodeParam.sortedInfo;
      state.requestUnlockCodeParam.filteredInfo = initialState.requestUnlockCodeParam.filteredInfo;
    },
    showModal: (state, action: PayloadAction<UNLOCK_CODE_MODAL>) => {
      state.unlockCodeDetailsModal = action.payload;
    },
    setUnlockCodesDetails: (state, action: PayloadAction<UnlockCodeDetails>) => {
      state.unlockCodeDetails = action.payload;
    },
    closeUnlockCodeDetailsModal: (state) => {
      state.unlockCodeDetailsModal = UNLOCK_CODE_MODAL.IDLE;
      state.unlockCodeDetails = new UnlockCodeDetails();
    },
    setTableFiltersInfo: (state, action: PayloadAction<string>) => {
      state.tableFiltersInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnlockCodes.pending, (state, _action) => {
        state.unlockCodeApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchUnlockCodes.fulfilled, (state, action) => {
        state.unlock_codes = action.payload;
        state.unlockCodeApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchUnlockCodes.rejected, (state, _action) => {
        state.unlockCodeApiStatus = APIStatus.ERROR;
      })
      .addCase(exportUnlockCodes.fulfilled, (state, action) => {
        const headers =
          'total_count,id,unlock_code,org_code,org_name,org_timezone_name,active,chat,expiration_date,unique_use_count,max_use_count,description,created,cohort_names,cohort_ids,cohort_count';
        state.csv = getCSV(action.payload, headers.split(','));
      });
  },
});

export const {
  handleManagerTableChange,
  clearFilter,
  showModal,
  setUnlockCodesDetails,
  closeUnlockCodeDetailsModal,
  setTableFiltersInfo,
} = adminUnlockCodesSlice.actions;
